<script setup lang="ts">
defineProps<{
  disabled?: boolean;
}>();

const model = defineModel<boolean>({ default: false });
</script>

<template>
  <label
    :class="[$style.checkbox, { [$style.checkbox_disabled!]: disabled }]"
    :aria-disabled="disabled"
  >
    <input
      v-model="model"
      type="checkbox"
      :disabled="disabled"
      :class="$style.input"
    />
    <span :class="$style.control" aria-hidden="true" />
    <!-- A slot rather than a label prop: the consents of the site carry links -->
    <span :class="$style.text"><slot /></span>
  </label>
</template>

<style module lang="scss">
.checkbox {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-1-5x);
  cursor: pointer;

  &_disabled {
    cursor: default;
    opacity: 0.6;
  }
}

// Kept in the tab order and reachable by screen readers, just not drawn
.input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}

.control {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  // Half of the 20px box against a 140% line, so the box sits on the first line
  margin-top: 1px;
  border: 1px solid var(--color-gray-line);
  border-radius: 2px;
  background-color: var(--color-white);

  .checkbox:hover & {
    border-color: var(--color-gray-2);
  }

  .input:focus-visible + & {
    outline: 2px solid var(--color-black-1);
    outline-offset: 2px;
  }

  .input:checked + & {
    border-color: var(--color-black-1);
    background-color: var(--color-black-1);
  }

  // The tick itself: two borders of a rotated box, the way the panel draws it
  .input:checked + &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 6px;
    width: 5px;
    height: 10px;
    border-right: 2px solid var(--color-white);
    border-bottom: 2px solid var(--color-white);
    transform: rotate(45deg);
  }
}

.text {
  @include text_body2;
  color: var(--color-black-1);

  a {
    color: var(--color-red-1);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
